import { SocialPlatform } from "@digital-twins/shared-types";
import { cn } from "@/lib/utils";
import { SocialPlatformIcon } from "../SocialPlatformIcon";
import { SOCIAL_PLATFORM_TEXT_CLASS, socialPlatformColorStyle } from "./socialChartColors";

export interface SocialBarListRow {
  key: string;
  label: string;
  count: number;
  /** Bar width as a percentage, 0 to 100, from `socialTopicMetrics`; `null` draws no bar. */
  share: number | null;
  /** The figure printed at the end of the row, already formatted. */
  valueLabel: string;
  /** Draws the channel mark and its brand color. */
  platform?: SocialPlatform;
  isSelected?: boolean;
  /**
   * The row has no value in the current measure (a channel that reports no
   * views): `noValueLabel` replaces the bar, and `valueLabel` carries the
   * post count instead of a fake zero.
   */
  hasNoValue?: boolean;
  /** A second, muted line under `valueLabel` (the share, "34%"). */
  detailLabel?: string;
  /** Another row is the focus (a filter or a hover elsewhere): the row fades. */
  isDimmed?: boolean;
}

interface SocialBarListProps {
  rows: Array<SocialBarListRow>;
  /** Rows become toggle buttons that report their key. */
  onSelect?: (key: string) => void;
  /** Hovering or focusing a row reports its key; leaving reports null. */
  onHighlight?: (key: string | null) => void;
  /** Row ids are `${testIdPrefix}-${index}`, bars `${testIdPrefix}-fill-${index}`. */
  testIdPrefix: string;
  ariaLabel?: string;
  emptyLabel?: string;
  /** Shown in place of the bar on `hasNoValue` rows ("No views reported"). */
  noValueLabel?: string;
}

function barWidth(share: number | null): string {
  if (share == null || !Number.isFinite(share)) {
    return "0%";
  }
  return `${Math.min(100, Math.max(0, share))}%`;
}

/**
 * Horizontal bars in HTML and CSS (plan D8): the channel split. The bar is
 * the theme primary and only the channel mark carries the platform color.
 * Widths animate, except under reduced motion.
 */
export function SocialBarList(props: SocialBarListProps) {
  const { rows, onSelect, onHighlight, testIdPrefix, ariaLabel, emptyLabel, noValueLabel } = props;

  if (rows.length === 0) {
    return (
      <p data-testid={`${testIdPrefix}-empty`} className="text-sm text-muted-foreground">
        {emptyLabel}
      </p>
    );
  }

  return (
    <ul aria-label={ariaLabel} className="space-y-1">
      {rows.map((row, index) => {
        const content = (
          <>
            <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
              {row.platform ? (
                <span
                  className={SOCIAL_PLATFORM_TEXT_CLASS}
                  style={socialPlatformColorStyle(row.platform)}
                >
                  <SocialPlatformIcon platform={row.platform} />
                </span>
              ) : null}
              <span className="truncate">{row.label}</span>
            </span>
            {row.hasNoValue ? (
              <span
                data-testid={`${testIdPrefix}-no-value-${index}`}
                className="truncate text-xs text-muted-foreground"
              >
                {noValueLabel}
              </span>
            ) : (
              <span className="relative h-1.5 overflow-hidden rounded-full bg-[hsl(var(--chart-bar-track))]">
                <span
                  data-testid={`${testIdPrefix}-fill-${index}`}
                  className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-500 ease-out motion-reduce:transition-none"
                  style={{ width: barWidth(row.share) }}
                />
              </span>
            )}
            <span className="text-right text-sm leading-tight tabular-nums">
              <span
                className={cn(
                  "block",
                  row.hasNoValue ? "text-xs text-muted-foreground" : "font-semibold text-foreground",
                )}
              >
                {row.valueLabel}
              </span>
              {row.detailLabel ? (
                <span className="block text-xs text-muted-foreground">{row.detailLabel}</span>
              ) : null}
            </span>
          </>
        );
        const rowClass = cn(
          "grid w-full grid-cols-[minmax(0,7rem)_minmax(0,1fr)_4rem] items-center gap-3 rounded-md px-2 py-1.5 transition-opacity",
          row.isDimmed && "opacity-35",
        );
        return (
          <li key={row.key}>
            {onSelect ? (
              <button
                type="button"
                data-testid={`${testIdPrefix}-${index}`}
                aria-pressed={row.isSelected ? "true" : "false"}
                onClick={() => onSelect(row.key)}
                onMouseEnter={() => onHighlight?.(row.key)}
                onMouseLeave={() => onHighlight?.(null)}
                onFocus={() => onHighlight?.(row.key)}
                onBlur={() => onHighlight?.(null)}
                className={cn(
                  rowClass,
                  "text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  row.isSelected && "ring-1 ring-primary/40",
                )}
              >
                {content}
              </button>
            ) : (
              <div
                data-testid={`${testIdPrefix}-${index}`}
                className={rowClass}
                onMouseEnter={() => onHighlight?.(row.key)}
                onMouseLeave={() => onHighlight?.(null)}
              >
                {content}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
